<template>
  <div class="container">
    <h3 class="mb-3">Import Students</h3>

    <form @submit.prevent="handleImport">
      <!-- Choix entre un groupe existant ou un nouveau -->
      <div class="row mb-3">
        <div class="col-md-6">
          <div class="form-floating">
            <select v-model="groupOption" class="form-select">
              <option value="new">Créer un nouveau groupe</option>
              <option value="existing">Ajouter à un groupe existant</option>
            </select>
            <label>Choix du groupe</label>
          </div>
        </div>
      </div>

      <!-- Sélection d'un groupe existant -->
      <div v-if="groupOption === 'existing'" class="row mb-3">
        <div class="col-md-6">
          <div class="form-floating">
            <select v-model="selectedGroup" id="GroupSelect" class="form-select" aria-label="Floating label select group" >
              <option v-for="group in groups" :key="group._id" :value="group._id">
                {{ group.groupName }}
              </option>
            </select>
            <label  for="GroupSelect">Sélectionner un groupe</label>
          </div>
          <div v-if="isSimulationGroup" class="form-text text-warning">
            🤖 Ce groupe sera marqué comme groupe de simulation car des agents ont été détectés dans l'import
          </div>
        </div>
      </div>

      <!-- Création d'un nouveau groupe -->
      <div v-if="groupOption === 'new'" class="row mb-3">
        <div class="col-md-6">
          <div class="form-floating">
            <input type="text" id="groupName" v-model="groupName" class="form-control" placeholder="Group Name"
              required />
            <label>Nom du groupe</label>
          </div>
          <div v-if="isSimulationGroup" class="form-text text-info">
            🤖 Le nom du groupe sera automatiquement modifié en "🤖_{{ groupName }}" car des agents ont été détectés
          </div>
        </div>

        <div class="col-md-6">
          <div class="form-floating">
            <input type="text" id="groupDescription" v-model="groupDescription" class="form-control"
              placeholder="Group Description" />
            <label>Description du groupe (optionnel)</label>
          </div>
        </div>
      </div>

      <!-- Informations du groupe -->
      <div class="row mb-3">
        <div class="col-md-4">
          <div class="form-floating">
            <input type="text" id="groupInstitution" v-model="groupInstitution" class="form-control"
              placeholder="Institution" required />
            <label>Institution</label>
          </div>
        </div>

        <div class="col-md-4">
          <div class="form-floating">
            <input type="text" id="groupLocation" v-model="groupLocation" class="form-control" placeholder="Location" />
            <label>Localisation</label>
          </div>
        </div>

        <div class="col-md-4">
          <div class="form-floating">
            <input type="text" id="groupZipcode" v-model="groupZipcode" class="form-control" placeholder="Zipcode" />
            <label>Code postal</label>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <div class="form-floating">
            <input type="text" id="groupGrade" v-model="groupGrade" class="form-control" placeholder="Grade" />
            <label>Niveau</label>
          </div>
        </div>

        <div class="col">
          <div class="form-floating">
            <input type="text" id="groupSector" v-model="groupSector" class="form-control" placeholder="Sector" />
            <label>Spécialité</label>
          </div>
        </div>
      </div>

      <!-- Import du fichier CSV -->
      <div class="mb-3 mt-3">
        <label class="form-label"><b>Import Students (CSV)</b></label>
        <div class="mb-2">
          <a href="#" @click.prevent="toggleCsvInfo" class="text-decoration-none d-flex align-items-center">
            <span class="me-2 csv-caret" :class="{ 'rotated': showCsvInfo }">▶</span>
            Check the CSV structure
          </a>
        </div>
        <div v-show="showCsvInfo" class="alert alert-info">
          <h6>Required CSV structure:</h6>
          <p><strong>Delimiter:</strong> semicolon (;)</p>
          <p><strong>Required headers:</strong></p>
          <ul class="mb-0">
            <li><code>firstname</code> - Student's first name</li>
            <li><code>lastname</code> - Student's last name</li>
            <li><code>email</code> - Email address (unique identifier)</li>
            <li><code>institution</code> - Student's institution</li>
            <li><code>sector</code> - Field/sector of studies</li>
            <li><code>class</code> - Student's class</li>
            <li><code>grade</code> - Grade level</li>
            <li><code>location</code> - Location</li>
            <li><code>zipcode</code> - Zip code</li>
            <li><code>mailStatus</code> - Email status (optional)</li>
            <li><code>password</code> - Password (optional)</li>
            <li><code>agent</code> - true/false - Is this an AI agent (optional)</li>
            <li><code>agentLevel</code> - A/B/C - Agent performance level (optional)</li>
          </ul>
        </div>
        <input type="file" id="studentFile" class="form-control" accept=".csv" @change="handleFileUpload" />
      </div>

      <!-- Aperçu des données importées -->
      <div v-if="studentsPreview.length > 0" class="mt-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">
            Prévisualisation des étudiants importés ({{ studentsPreview.length }} total)
            <span v-if="isSimulationGroup" class="badge bg-info ms-2">
              🤖 Groupe de simulation détecté
            </span>
          </h5>
          <!-- Bouton de validation en haut -->
          <button type="submit" class="btn btn-primary" :disabled="studentsPreview.length === 0 || isImporting">
            <span v-if="isImporting" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ isImporting ? 'Importation en cours...' : 'Importer les étudiants' }}
          </button>
        </div>

        <!-- Informations sur l'affichage limité -->
        <div v-if="hasMoreStudents && !showAllPreview" class="alert alert-info">
          <i class="bi bi-info-circle me-2"></i>
          <strong>Affichage limité :</strong> 
          Seuls les {{ previewLimit }} premiers étudiants sont affichés pour des raisons de performance.
          <button type="button" class="btn btn-sm btn-outline-primary ms-2" @click="toggleShowAll">
            Afficher tout ({{ studentsPreview.length }})
          </button>
        </div>

        <div v-if="showAllPreview && hasMoreStudents" class="alert alert-warning">
          <i class="bi bi-exclamation-triangle me-2"></i>
          <strong>Affichage complet :</strong> 
          {{ studentsPreview.length }} étudiants affichés. Cela peut impacter les performances.
          <button type="button" class="btn btn-sm btn-outline-secondary ms-2" @click="toggleShowAll">
            Affichage limité ({{ previewLimit }})
          </button>
        </div>

        <table class="table table-striped">
          <thead>
            <tr>
              <th>#</th>
              <th>Prénom</th>
              <th>Nom</th>
              <th>Email</th>
              <th>Institution</th>
              <th>Spécialité</th>
              <th>Niveau</th>
              <th>Localisation</th>
              <th v-if="isSimulationGroup">Agent</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(student, index) in studentsPreviewDisplay" :key="index">
              <td>{{ index + 1 }}</td>
              <td>{{ student.firstname }}</td>
              <td>{{ student.lastname }}</td>
              <td>{{ student.email }}</td>
              <td>{{ student.institution }}</td>
              <td>{{ student.sector }}</td>
              <td>{{ student.grade }}</td>
              <td>{{ student.location }}</td>
              <td v-if="isSimulationGroup">
                <span v-if="student.agent" class="badge bg-success">🤖 Agent {{ student.agentLevel || 'A' }}</span>
                <span v-else class="badge bg-secondary">👤 Humain</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Informations sur les étudiants non affichés -->
        <div v-if="hasMoreStudents && !showAllPreview" class="text-muted text-center mt-2">
          ... et {{ studentsPreview.length - previewLimit }} autre(s) étudiant(s)
          <button type="button" class="btn btn-sm btn-link" @click="toggleShowAll">
            Voir tous les étudiants
          </button>
        </div>
      </div>

      <!-- Bouton pour soumettre -->
      <div class="d-flex justify-content-end">
        <button type="submit" class="btn btn-primary" :disabled="studentsPreview.length === 0 || isImporting">
          <span v-if="isImporting" class="spinner-border spinner-border-sm me-2" role="status"></span>
          {{ isImporting ? 'Importation en cours...' : 'Importer les étudiants' }}
        </button>
      </div>
    </form>

    <!-- Overlay d'importation -->
    <div v-if="isImporting" class="import-overlay d-flex align-items-center justify-content-center">
      <div class="import-progress-card">
        <div class="text-center mb-3">
          <div class="spinner-border text-primary mb-3" role="status" style="width: 3rem; height: 3rem;">
            <span class="visually-hidden">Chargement...</span>
          </div>
          <h5 class="mb-2">{{ importProgress.step }}</h5>
          <div class="progress mb-2" style="height: 20px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" 
                 :style="`width: ${importProgress.percentage}%`">
              {{ Math.round(importProgress.percentage) }}%
            </div>
          </div>
          <small class="text-muted">
            Étape {{ importProgress.current }} sur {{ importProgress.total }}
          </small>
        </div>
      </div>
    </div>

    <!-- Modal de confirmation -->
    <div v-if="showConfirmationModal" class="modal d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i :class="importResults.success ? 'bi bi-check-circle-fill text-success me-2' : 'bi bi-exclamation-triangle-fill text-warning me-2'"></i>
              Import {{ importResults.success ? 'réussi' : 'échoué' }}
            </h5>
          </div>
          <div class="modal-body">
            <div v-if="importResults.success">
              <div class="alert alert-success d-flex align-items-center">
                <i class="bi bi-check-circle-fill me-2"></i>
                <div>
                  <strong>{{ importResults.studentsCreated }}</strong> étudiant(s) créé(s) avec succès
                  <span v-if="importResults.studentsSkipped > 0">
                    <br><small class="text-muted">
                      {{ importResults.studentsSkipped }} étudiant(s) ignoré(s) (déjà existant)
                    </small>
                  </span>
                </div>
              </div>
              <div v-if="importResults.studentsCreated > 0" class="row">
                <div class="col-md-6">
                  <h6>Groupe :</h6>
                  <p class="mb-1"><strong>{{ importResults.groupName }}</strong></p>
                  <small class="text-muted">ID: {{ importResults.groupId }}</small>
                </div>
                <div v-if="isSimulationGroup" class="col-md-6">
                  <div class="alert alert-info">
                    <i class="bi bi-robot me-2"></i>
                    <strong>Groupe de simulation</strong><br>
                    <small>Des agents IA ont été détectés dans l'import</small>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="alert alert-warning">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <div>
                <strong>{{ importResults.message }}</strong>
                <div v-if="importResults.studentsSkipped > 0" class="mt-2">
                  <small class="text-muted">
                    <strong>Détails :</strong> {{ importResults.studentsSkipped }} étudiant(s) dans le fichier CSV étaient déjà présents dans la base.
                  </small>
                </div>
                <div class="mt-3">
                  <small class="text-muted">
                    <strong>Solutions possibles :</strong>
                    <ul class="mt-1 mb-0">
                      <li>Vérifiez que le fichier CSV contient les colonnes requises : firstname, lastname, email, institution, etc.</li>
                      <li>Utilisez le délimiteur point-virgule (;) dans le fichier CSV</li>
                      <li>Assurez-vous que les emails sont uniques et ne sont pas déjà dans la base</li>
                    </ul>
                  </small>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="closeConfirmationModal">
              {{ importResults.success ? 'Continuer' : 'Fermer' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import Papa from "papaparse";
import { ref, onMounted, computed } from "vue";
import api from "@/services/axios";

// Définir les événements émis vers le parent
const emit = defineEmits(['group-list-updated']);

// Import authorize to includes Agents / fake student used by the simulation system!
// États pour les données
const groupOption = ref("new"); // "new" pour créer un groupe, "existing" pour sélectionner un groupe existant
const groups = ref([]);
const selectedGroup = ref("");
const groupName = ref("");
const groupDescription = ref("");
const groupInstitution = ref("");
const groupLocation = ref("");
const groupZipcode = ref("");
const groupGrade = ref("");
const groupSector = ref("");
const studentsPreview = ref([]);
const previewLimit = ref(100); // Limite d'affichage pour éviter les problèmes de performance
const showAllPreview = ref(false); // Toggle pour afficher tous les étudiants
const file = ref(null);
const showCsvInfo = ref(false);
const isSimulationGroup = ref(false); // Indique si le groupe contient des agents de simulation

// États pour l'animation d'attente et la confirmation
const isImporting = ref(false);
const importProgress = ref({
  step: '',
  current: 0,
  total: 0,
  percentage: 0
});
const showConfirmationModal = ref(false);
const importResults = ref({
  success: false,
  studentsCreated: 0,
  studentsSkipped: 0,
  groupName: '',
  groupId: '',
  message: ''
});

// Charger la liste des groupes existants
onMounted(async () => {
  try {
    const response = await api.get(`/groups/list`);
groups.value = response.data; // L'API retourne directement la liste des groupes
console.log("Groupes chargés :", groups.value); // Debugging
  } catch (error) {
    console.error("Error fetching groups:", error);
  }
});

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return ["true","1","yes","oui","y","o"].includes(s);
};

// Computed pour l'affichage limité de la prévisualisation
const studentsPreviewDisplay = computed(() => {
  if (showAllPreview.value || studentsPreview.value.length <= previewLimit.value) {
    return studentsPreview.value;
  }
  return studentsPreview.value.slice(0, previewLimit.value);
});

// Computed pour savoir s'il y a plus d'étudiants à afficher
const hasMoreStudents = computed(() => {
  return studentsPreview.value.length > previewLimit.value;
});

// Fonction pour basculer l'affichage complet
const toggleShowAll = () => {
  showAllPreview.value = !showAllPreview.value;
};

// Gestion de l'import du fichier CSV
const handleFileUpload = (event) => {
  file.value = event.target.files[0];
  // Réinitialiser l'état d'affichage
  showAllPreview.value = false;
  
  if (file.value) {
    Papa.parse(file.value, {
      delimiter: ";",
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        studentsPreview.value = results.data.map((row) => ({
          firstname: row.firstname || "",
          lastname: row.lastname || "",
          email: row.email || "",
          institution: row.institution || "",
          sector: row.sector || "",
          class: row.class || "",
          grade: row.grade || "",
          location: row.location || "",
          zipcode: row.zipcode || "",
          mailStatus: row.mailStatus || "",
          password: row.password || "",
          agent: toBool(row.agent),
          agentLevel: row.agentLevel ? String(row.agentLevel).trim() : "",
        }));

        // Vérifier si les 10 premières lignes contiennent des agents
        const first10Rows = results.data.slice(0, 10);
        const hasAgents = first10Rows.some(row => toBool(row.agent));
        isSimulationGroup.value = hasAgents;

        if (hasAgents) {
          console.log('🤖 Détection d\'agents de simulation dans le fichier CSV');
        }
      },
      error: (error) => {
        console.error("Error parsing CSV file:", error);
      },
    });
  }
};

// Gestion de l'import des étudiants avec vérification des doublons
const handleImport = async () => {
  if (studentsPreview.value.length === 0) {
    alert("Please import students before submitting.");
    return;
  }

  // Démarrer l'animation d'attente
  isImporting.value = true;
  importProgress.value = {
    step: 'Initialisation de l\'import...',
    current: 1,
    total: 6,
    percentage: 0
  };

  try {
    const studentIds = [];
    let studentsCreated = 0;
    let studentsSkipped = 0;

    // Étape 1: Vérification et création des étudiants
    importProgress.value = {
      step: 'Vérification des étudiants...',
      current: 1,
      total: 6,
      percentage: 16
    };

    for (const student of studentsPreview.value) {
      // Vérification des doublons (email existant)
      const existingStudent = await api.get(`/students/check?email=${student.email}`);
      if (existingStudent.data.exists) {
        console.warn(`Student with email ${student.email} already exists, skipping.`);
        studentsSkipped++;
        continue; // Ne pas réimporter l'étudiant
      }

      // Création de l'étudiant s'il n'existe pas
      const response = await api.post(`/students`, student);
      studentIds.push(response.data.student._id);
      studentsCreated++;
    }

    // Étape 2: Gestion du groupe
    importProgress.value = {
      step: 'Création/mise à jour du groupe...',
      current: 2,
      total: 6,
      percentage: 33
    };

    let groupId = selectedGroup.value;
    let finalGroupName = '';

    // Si l'utilisateur crée un nouveau groupe ET qu'au moins un étudiant a été créé
    if (groupOption.value === "new" && studentsCreated > 0) {
      // Modifier le nom du groupe si des agents sont détectés
      finalGroupName = groupName.value;
      if (isSimulationGroup.value) {
        finalGroupName = `🤖_${groupName.value}`;
      }

      // Étape 3: Création du nouveau groupe
      importProgress.value = {
        step: 'Création du nouveau groupe...',
        current: 3,
        total: 6,
        percentage: 50
      };

      const groupResponse = await api.post(`/groups`, {
        groupName: finalGroupName,
        description: groupDescription.value,
        institution: groupInstitution.value,
        location: groupLocation.value,
        zipCode: groupZipcode.value,
        grade: groupGrade.value,
        sector: groupSector.value,
        students: studentIds,
        simulation: isSimulationGroup.value, // Ajouter la propriété simulation
      });
      groupId = groupResponse.data.group._id;
    } else if (groupOption.value === "new" && studentsCreated === 0) {
      // Si aucun étudiant n'a été créé, pas de création de groupe
      finalGroupName = groupName.value; // Pour l'affichage dans les résultats
      groupId = null;
    }

     // 🆕 Si l'utilisateur ajoute des étudiants à un groupe existant ET qu'au moins un étudiant a été créé
     if (groupOption.value === "existing" && studentsCreated > 0) {
      // Étape 3: Mise à jour du groupe existant
      importProgress.value = {
        step: 'Ajout au groupe existant...',
        current: 3,
        total: 6,
        percentage: 50
      };

      await api.put(`/groups/${selectedGroup.value}/add-students`, {
        students: studentIds, // Envoi la liste des _id des étudiants
      });

      // Récupérer le nom du groupe existant
      const existingGroup = groups.value.find(g => g._id === selectedGroup.value);
      finalGroupName = existingGroup ? existingGroup.groupName : 'Groupe existant';

      // Si des agents sont détectés, marquer le groupe existant comme groupe de simulation
      if (isSimulationGroup.value) {
        await api.put(`/groups/${selectedGroup.value}`, {
          simulation: true
        });
        console.log('🤖 Groupe existant marqué comme groupe de simulation');
      }
    } else if (groupOption.value === "existing" && studentsCreated === 0) {
      // Si aucun étudiant n'a été créé, récupérer juste le nom pour l'affichage
      const existingGroup = groups.value.find(g => g._id === selectedGroup.value);
      finalGroupName = existingGroup ? existingGroup.groupName : 'Groupe existant';
    }

    // Étape 4: Association des étudiants au groupe (seulement si des étudiants ont été créés et un groupe existe)
    if (studentsCreated > 0 && groupId) {
      importProgress.value = {
        step: 'Association des étudiants au groupe...',
        current: 4,
        total: 6,
        percentage: 66
      };

      for (const studentId of studentIds) {
        await api.post(`/students/${studentId}/add-group`, { groupId });
      }
    } else {
      // Passer directement à l'étape suivante si pas d'association nécessaire
      importProgress.value = {
        step: 'Aucune association nécessaire...',
        current: 4,
        total: 6,
        percentage: 66
      };
    }

    // Étape 5: Finalisation
    importProgress.value = {
      step: 'Finalisation...',
      current: 5,
      total: 6,
      percentage: 83
    };

    // Petit délai pour l'effet visuel
    await new Promise(resolve => setTimeout(resolve, 500));

    // Étape 6: Terminé
    importProgress.value = {
      step: 'Import terminé !',
      current: 6,
      total: 6,
      percentage: 100
    };

    // Analyser les résultats d'import
    let success = true;
    let message = 'Import réalisé avec succès !';

    // Vérifier si aucun étudiant n'a été créé
    if (studentsCreated === 0) {
      success = false;
      if (studentsSkipped === studentsPreview.value.length) {
        message = 'Aucun étudiant importé : Tous les étudiants du fichier CSV sont déjà présents dans la base de données.';
      } else if (studentsPreview.value.length === 0) {
        message = 'Aucun étudiant importé : Le fichier CSV semble vide ou mal formaté.';
      } else {
        message = 'Aucun étudiant importé : Vérifiez le format du fichier CSV et les données requises.';
      }
    } else if (studentsSkipped > 0) {
      message = `Import partiellement réussi : ${studentsCreated} étudiant(s) créé(s), ${studentsSkipped} étudiant(s) ignoré(s) (déjà existants).`;
    }

    // Préparer les résultats pour la modal de confirmation
    importResults.value = {
      success,
      studentsCreated,
      studentsSkipped,
      groupName: finalGroupName,
      groupId,
      message
    };

    // Réinitialisation des champs après succès
    groupName.value = "";
    groupDescription.value = "";
    groupInstitution.value = "";
    groupLocation.value = "";
    groupZipcode.value = "";
    groupGrade.value = "";
    groupSector.value = "";
    studentsPreview.value = [];
    file.value = null;
    
    // Arrêter l'animation et afficher la modal de confirmation
    isImporting.value = false;
    showConfirmationModal.value = true;
    
    // Émettre événement pour mettre à jour la liste des groupes seulement si des étudiants ont été ajoutés/créés
    if (studentsCreated > 0) {
      emit('group-list-updated');
    }

  } catch (error) {
    console.error("Error importing students:", error.response?.data || error.message);
    
    // Préparer les résultats d'erreur
    importResults.value = {
      success: false,
      studentsCreated: 0,
      studentsSkipped: 0,
      groupName: '',
      groupId: '',
      message: error.response?.data?.message || error.message || 'Une erreur inconnue est survenue'
    };
    
    // Arrêter l'animation et afficher la modal de confirmation (erreur)
    isImporting.value = false;
    showConfirmationModal.value = true;
  }
};

// Fonction pour basculer l'affichage des informations CSV
const toggleCsvInfo = () => {
  showCsvInfo.value = !showCsvInfo.value;
};

// Fonction pour fermer la modal de confirmation
const closeConfirmationModal = () => {
  showConfirmationModal.value = false;
  // Recharger la liste des groupes seulement si l'import a réussi ET créé des étudiants
  // (nouveau groupe ou ajout à groupe existant)
  if (importResults.value.success && importResults.value.studentsCreated > 0) {
    // Recharger la liste des groupes via l'événement parent
    emit('group-list-updated');
  }
};
</script>


<style scoped>
.table {
  margin-top: 1rem;
}

.container {
  border: 1px gainsboro solid;
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 20px;
  background-color: rgb(244, 226, 246);
}

.csv-caret {
  transition: transform 0.2s ease;
}

.csv-caret.rotated {
  transform: rotate(90deg);
}

.import-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9999;
}

.import-progress-card {
  background: white;
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  min-width: 300px;
  max-width: 400px;
}

.progress {
  background-color: #e9ecef;
}

.progress-bar {
  background-color: #0d6efd;
  transition: width 0.3s ease;
}

.modal {
  z-index: 10000;
}
</style>